import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { DependencyResponse } from '../../../../shared/dependenciesV2';

import { getBaseDependencyFetchQueryKey } from './queryKey';

/**
 * Without a `revisionScanId`, this is a prefix of the keys for every revision scan of the dependency,
 * so invalidating it covers all of them.
 */
export const getProjectDependencyQueryKey = (
  projectRevisionLocator: string,
  dependencyRevisionLocator: string,
  revisionScanId?: number,
): QueryKey => [
  ...getBaseDependencyFetchQueryKey(dependencyRevisionLocator),
  'revisions',
  projectRevisionLocator,
  ...(revisionScanId ? [revisionScanId] : []),
];

/**
 * Hook to fetch a project dependency.
 * @param projectRevisionLocator - The locator of the project revision.
 * @param dependencyRevisionLocator - The locator of the dependency revision.
 * @param shouldFetch - Whether to fetch the dependency.
 * @param revisionScanId - The revision scan to read issues from. Defaults to the revision's latest scan.
 * @returns The dependency.
 */
export default function useGetProjectDependency(
  projectRevisionLocator: string,
  dependencyRevisionLocator: string,
  shouldFetch: boolean = true,
  revisionScanId?: number,
) {
  const queryKey: QueryKey = getProjectDependencyQueryKey(
    projectRevisionLocator,
    dependencyRevisionLocator,
    revisionScanId,
  );

  const queryFn = () => get<DependencyResponse>(
    `/api/v2/revisions/${nuc(projectRevisionLocator)}/dependencies/${nuc(dependencyRevisionLocator)}`,
    false,
    revisionScanId ? { revisionScanId } : undefined,
  );

  return useQuery<DependencyResponse>({
    queryKey,
    queryFn,
    enabled: shouldFetch,
  });
}
